import React, { useEffect, useMemo, useState } from 'react';

import Button from 'epn-ui/umd/dist/components/Button';
import Checkbox from 'epn-ui/umd/dist/components/CheckboxInput';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import Input from 'epn-ui/umd/dist/components/InputText';
import Loader from 'epn-ui/umd/dist/components/Loader';
import notification from 'epn-ui/umd/dist/components/Notification';
import Select from 'epn-ui/umd/dist/components/Select';
import { toast } from 'react-toastify';

import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as Sentry from '@sentry/nextjs';
import map from 'lodash/map';
import reduce from 'lodash/reduce';

import BaseEmpty from '@components/base/BaseEmpty';

import useI18n from '@i18n';
import copyTextToClipboard from '@utils/clipboard';
import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from 'src/utils/metrics/amplitude';
import sendGaEvent from 'src/utils/metrics/ga';

import BaseToast from '@base/BaseToast';

import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import appConfig from '@config/app.json';

import {
    IGetCreativesDataFormatted,
    IGetSubsDataFormatted,
    IPostLinkReductionDataFormatted,
} from '@api/types';

import {
    ALI_ID,
    ALI_PUB_DATA,
    GOT_BY_DATA,
    aliexpressGoodsRegexp,
    aliexpressRegexp,
    skuIdRegexp,
} from '../../config';

const { useSubs, useReduceLinks } = apiHooks;

const DEFAULT_SUBS = {
    sub: null,
    sub2: null,
    sub3: null,
    sub4: null,
    sub5: null,
};

export interface IModalDeepLinkProps {
    data: IGetCreativesDataFormatted;
    onClose: () => void;
    checkedReduction: boolean;
    setCheckedReduction: (value: boolean) => void;
    activeReduction: { label: string; value: string };
    setActiveReduction: ({ label, value }: { label: string; value: string }) => void;
}

const ModalDeeplink: React.FC<IModalDeepLinkProps> = ({
    data: { code, hash, offerId, erid },
    onClose,
    checkedReduction,
    setCheckedReduction,
    activeReduction,
    setActiveReduction,
}) => {
    const isRefOfferId = appConfig.REF_OFFERS_IDS.includes(+offerId);
    const linkWithHash = isRefOfferId ? `${code}&creative_hash=${hash}` : code;

    const { t, locale } = useI18n('Creatives');
    const { t: tCommon } = useI18n('Common');
    const { t: tTools } = useI18n('Tools');
    const [link, setLink] = useState('');
    const [deeplink, setDeeplink] = useState(linkWithHash);
    const [subs, setSubs] = useState(DEFAULT_SUBS);
    const isAliexpressOffer = +offerId === appConfig.OFFER_IDS.ALI;

    const defaultReduction = +offerId === ALI_ID ? ALI_PUB_DATA : GOT_BY_DATA;

    useEffect(() => {
        if (!checkedReduction) {
            setActiveReduction(defaultReduction);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const linkReductionOptions = [ALI_PUB_DATA, GOT_BY_DATA];

    const subsParams = { subFields: 'sub,sub2,sub3,sub4,sub5' };

    const { data: subsData = [], error: subsError, isLoading: isFetchingSubs } = useSubs({
        params: subsParams,
        dependencies: [locale],
    }) as ICreateUseRequestInfiniteResponse<IGetSubsDataFormatted[]>;

    const reduceLinkParams = useMemo(
        () => ({
            urlContainer: [
                {
                    url: deeplink,
                    domainCutter: activeReduction.value || activeReduction,
                },
            ],
        }),
        [deeplink, activeReduction],
    );

    const {
        data: reductionData = [],
        error: reductionError,
        isLoading: isFetchingReduction,
    } = useReduceLinks<IPostLinkReductionDataFormatted[]>({
        params: reduceLinkParams,
        condition: checkedReduction,
        dependencies: [activeReduction, linkWithHash],
    });

    const reductionLink = reductionData[0]?.link;

    const formattedSubs = reduce(
        reduce(
            subsData || {},
            (result, { subs: subData }) =>
                reduce(
                    subData,
                    (prev, value, name) => {
                        if (!value)
                            return {
                                ...prev,
                                [name]: prev[name] || new Set(),
                            };
                        return prev[name]
                            ? prev[name].add(value) && prev
                            : { ...prev, [name]: new Set([value]) };
                    },
                    result,
                ),
            {},
        ),
        (result, set, key) => ({ ...result, [key]: Array.from(set) }),
        {},
    );

    const subsForSelect = useMemo(
        () => (Object.keys(subsData).length ? formattedSubs : subs),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [subsData],
    );

    const selectData = useMemo(
        () =>
            map(subsForSelect, (data, name) => ({
                key: name,
                values: map(data, item => ({ value: item, label: item })),
            })),
        [subsForSelect],
    );

    const dataIsSet = link || Object.values(subs).some(item => item?.value);

    const onCopyMessage = async value => {
        await copyTextToClipboard(value);
        toast.success(<BaseToast text={t('Link copied')} type="success" />, {
            toastId: value,
        });
    };

    // const validateSub = (field, value) =>
    //     setError(
    //         !subIsValid(value) ? { [field]: 'Sub may only contain the following symbols' } : {},
    //     );

    const cleanLink = productLink => {
        if (+offerId !== ALI_ID) return productLink;
        const [domen] = productLink.match(aliexpressRegexp);
        const [product] = productLink.match(aliexpressGoodsRegexp);
        const [skuId] = productLink.match(skuIdRegexp) || [];
        return `${domen}${product}${skuId ? `?${skuId}` : ''}`;
    };

    const createDeeplink = (): void => {
        const params = reduce(
            subs,
            (res, value, key) => (value && [...res, [key, value.value]]) || res,
            [],
        );

        if (link) {
            const [, , productId = null] = link.match(aliexpressGoodsRegexp) || [];
            const cleanedLink = productId ? cleanLink(link) : link;
            params.push(['to', cleanedLink.toString()]);
        }

        setLink('');
        setSubs(DEFAULT_SUBS);
        setCheckedReduction(false);

        try {
            const redirectUrl = new URL(linkWithHash);
            const redirectUrlParams = new URLSearchParams(redirectUrl.search);

            params.forEach(param => {
                if (param[0] && param[1]) {
                    redirectUrlParams.set(param[0], param[1]);
                }
            });

            redirectUrl.search = redirectUrlParams.toString();

            setDeeplink(redirectUrl.toString());
            sendGaEvent({
                eventCategory: 'creative',
                eventAction: 'getLink',
            });
            logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.creativeLinkCreate);
            notification.success({
                message: t('Link is ready'),
            });
        } catch (e) {
            setDeeplink(linkWithHash);
            Sentry.captureException(e);
        }
    };

    /**
     * @param {string} token
     * @returns {void}
     */
    const onEridCopyHandler = async token => {
        if (typeof token !== 'string' || token.length === 0) return;
        await copyTextToClipboard(token);
        notification.success({
            message: tCommon('Copied'),
        });
    };

    const reductionRequestId = reductionError?.response?.headers?.get('request-id');
    const subsRequestId = subsError?.response?.headers?.get('request-id');

    return (
        <Dialog title={tCommon('Get link')} onCancel={onClose} open>
            <div id="modal-deep-link">
                {typeof erid === 'string' && erid.length > 0 && (
                    <div className="flex items-center mb-8">
                        <div className="truncate">
                            <span className="font-medium text-blueGray-600">{t('Token')}:</span>{' '}
                            <span
                                aria-hidden="true"
                                className="text-blueGray-500 hover:cursor-pointer"
                                onClick={() => onEridCopyHandler(erid)}
                            >
                                {erid}
                            </span>
                        </div>
                        <Button
                            icon={<FontAwesomeIcon icon={faCopy} />}
                            shape="circle"
                            size="small"
                            className="border-0 text-blueGray-300"
                            onClick={() => onEridCopyHandler(erid)}
                        />
                    </div>
                )}
                {!isAliexpressOffer && (
                    <div className="mb-6">
                        <Input
                            id="CreativeModalDeeplinkLinkInput"
                            value={link}
                            placeholder={t('Enter link to the product')}
                            onChange={e => setLink(e.target.value)}
                            disabled={isRefOfferId}
                        />
                    </div>
                )}
                {isFetchingSubs && !isAliexpressOffer && (
                    <div className="flex items-center justify-center">
                        <Loader />
                    </div>
                )}
                {subsError && !isAliexpressOffer && (
                    <div className="p-20 text-center text-p1">
                        {tCommon('Failed to load data')}{' '}
                        {subsRequestId && (
                            <p className="font-medium">request-id: {subsRequestId}</p>
                        )}
                    </div>
                )}
                {!isFetchingSubs &&
                    !subsError &&
                    !isAliexpressOffer &&
                    selectData.map(({ key, values }, index) => (
                        <div className="mb-6" key={key}>
                            <Select
                                placeholder={`${t('Enter value Sub')} ${index + 1}`}
                                id={`CreativeModalDeeplinkSubSelect${index}`}
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                options={values}
                                className="w-full"
                                value={subs[key]}
                                onChange={item => setSubs({ ...subs, [key]: item })}
                                notFoundContent={<BaseEmpty id="CreativesOffersSelect" />}
                            />
                        </div>
                    ))}
                {!isAliexpressOffer && (
                    <div className="mb-8">
                        <Button
                            type="primary"
                            id="CreativeModalDeeplinkApplyButton"
                            disabled={!dataIsSet}
                            onClick={createDeeplink}
                        >
                            {t('Apply')}
                        </Button>
                    </div>
                )}
                <div className="relative">
                    <Input
                        value={deeplink}
                        readOnly
                        suffix={
                            <FontAwesomeIcon
                                icon={faCopy}
                                onClick={() => onCopyMessage(deeplink)}
                            />
                        }
                        placeholder={t('Link')}
                        onClick={() => onCopyMessage(deeplink)}
                    />
                </div>
                {!isAliexpressOffer && (
                    <div className="flex flex-col items-baseline my-6 xs:flex-row xs:items-center">
                        <Checkbox
                            id="LinkReductionCheckbox"
                            label={tTools('Shorten link via')}
                            checked={checkedReduction}
                            onChange={() => setCheckedReduction(!checkedReduction)}
                        />

                        <div className="ml-5 w-28 xs:ml-2">
                            <Select
                                id="LinkReductionSelect"
                                value={activeReduction}
                                fieldNames={{
                                    label: 'label',
                                    value: 'value',
                                }}
                                onChange={setActiveReduction}
                                options={linkReductionOptions}
                                notFoundContent={<BaseEmpty />}
                            />
                        </div>
                    </div>
                )}
                {isFetchingReduction && checkedReduction && <Loader className="w-full" />}
                {checkedReduction &&
                    !isFetchingReduction &&
                    !reductionError &&
                    !isAliexpressOffer && (
                        <Input
                            value={reductionLink}
                            readOnly
                            suffix={
                                <FontAwesomeIcon
                                    icon={faCopy}
                                    onClick={() => onCopyMessage(reductionLink)}
                                />
                            }
                            placeholder={`${t('Shorten')} (${activeReduction.label})`}
                            onClick={() => onCopyMessage(reductionLink)}
                        />
                    )}
                {reductionError && (
                    <div className="p-20 text-center text-p1">
                        {t('Failed to get reduced link')}{' '}
                        {reductionRequestId && (
                            <p className="font-medium">request-id: {reductionRequestId}</p>
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default ModalDeeplink;
